import { useState, useEffect, useRef, useCallback } from 'react'
import { useMsal } from '@azure/msal-react'
import { useGoogleLogin, googleLogout } from '@react-oauth/google'
import { loginRequest } from './config/authConfig'
import { initializeAuth, setGoogleCredential, clearGoogleCredential, getCurrentAuthProvider, restoreGoogleCredential } from './services/api'
import { ChatProvider } from './contexts/ChatContext'
import { WorkspaceProvider } from './contexts/WorkspaceContext'
import { ProjectsProvider } from './contexts/ProjectsContext'
import { EncounterProvider } from './contexts/EncounterContext'
import { ThemeProvider, useTheme } from './contexts/ThemeContext'
import { WorkspaceManager } from './components/workspace/WorkspaceManager'
import { Button } from './components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './components/ui/tooltip'
import { Shield, Lock, Cloud, Moon, Sun, LogOut, Bell, FolderOpen, FileText, Sparkles, Download, Menu } from 'lucide-react'
import api from './services/api'
import { msalConfig } from './config/authConfig'
import { trackAuthEvent, trackAuthException } from './lib/telemetry'
import { clearStaleInteractionStatus, readInteractionStatus } from './lib/msalCleanup'
import { captureAuthError, consumePendingAuthError, friendlyAuthMessage, type AuthErrorInfo } from './lib/authError'
import { SettingsDialog } from './components/settings/SettingsDialog'
import { OnboardingWizard } from './components/onboarding'
import { AcleraWordmark } from './components/icons/AcleraWordmark'
import { useSessionTimeout } from './hooks/useSessionTimeout'
import { SessionTimeoutWarning } from './components/SessionTimeoutWarning'
import { useNotifications } from './hooks/useNotifications'
import { useViewportHeight } from './hooks/useViewportHeight'
import { cn } from './lib/utils'
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext'
import { UpgradeModal } from './components/subscription/UpgradeModal'

/**
 * Upgrade modal wrapper that uses subscription context
 * Must be rendered inside SubscriptionProvider
 */
const SubscriptionGate = ({ children }: { children: React.ReactNode }) => {
  const { showUpgradeModal, setShowUpgradeModal } = useSubscription()

  return (
    <>
      {children}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
      />
    </>
  )
}

/**
 * Theme toggle component with elegant switch design
 */
const ThemeToggle = () => {
  const { resolvedTheme, setTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={() => setTheme(isDark ? 'light' : 'dark')}
          className="relative flex items-center justify-center h-9 px-2 gap-1 rounded-lg bg-surface-elevated border border-border/50 hover:border-primary/30 transition-all duration-300 group"
          aria-label="Toggle dark mode"
        >
          <Sun className={`h-4 w-4 transition-all duration-300 ${isDark ? 'text-muted-foreground/40' : 'text-clinical-amber'}`} />
          <div className={`w-8 h-4 rounded-full transition-all duration-300 ${isDark ? 'bg-clinical-cyan/20' : 'bg-clinical-amber/20'} relative`}>
            <div className={`absolute top-0.5 w-3 h-3 rounded-full transition-all duration-300 ${isDark ? 'right-0.5 bg-clinical-cyan' : 'left-0.5 bg-clinical-amber'}`} />
          </div>
          <Moon className={`h-4 w-4 transition-all duration-300 ${isDark ? 'text-clinical-cyan' : 'text-muted-foreground/40'}`} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <p className="text-xs">{isDark ? 'Switch to light mode' : 'Switch to dark mode'}</p>
      </TooltipContent>
    </Tooltip>
  )
}



// Dev auth bypass — auto-authenticate in local dev without OAuth
const DEV_AUTH = import.meta.env.VITE_DEV_AUTH === 'true'
if (DEV_AUTH) {
  setGoogleCredential('dev-token')
}

function App() {
  // Keep --app-height synced to the visible viewport (handles the soft keyboard).
  useViewportHeight()

  const { instance, accounts } = useMsal()
  const [googleUser, setGoogleUser] = useState<any>(() =>
    DEV_AUTH ? { name: 'Dev User', email: 'dev@caseyspanel.local', givenName: 'Dev' } : null
  )
  const [isGoogleAuthenticated, setIsGoogleAuthenticated] = useState(DEV_AUTH)
  const [authError, setAuthError] = useState<AuthErrorInfo | null>(() => consumePendingAuthError())
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [onboardingChecked, setOnboardingChecked] = useState(DEV_AUTH)

  // Header workspace info — populated by WorkspaceManager via onHeaderChange callback
  const [headerInfo, setHeaderInfo] = useState<{ displayName: string | null, phase: string | null, type: string | null }>({ displayName: null, phase: null, type: null })

  // Files info — populated by WorkspaceManager via onFilesChange callback
  const [filesInfo, setFilesInfo] = useState<{
    uploadedFiles: Array<{ id: string; name: string; type: string; timestamp: string }>
    artifacts: Array<{ id: string; title: string; type: string; timestamp: string; artifactId: string }>
    workspaceId: string
  }>({ uploadedFiles: [], artifacts: [], workspaceId: '' })

  // Notification state — DB-persisted, loaded from hydration, written via SSE callback
  const { notifications, addNotification, markAsRead, markAllAsRead, clearAll: clearNotifications, loadFromHydration: loadNotifications, unreadCount } = useNotifications()

  // Notification / files dropdowns in the header
  const [showNotifications, setShowNotifications] = useState(false)
  const [showFiles, setShowFiles] = useState(false)
  const notificationsRef = useRef<HTMLDivElement>(null)
  const filesRef = useRef<HTMLDivElement>(null)
  const canvasTabRef = useRef<((tab: string) => void) | null>(null)
  const [mobileMenuHandler, setMobileMenuHandler] = useState<(() => void) | null>(null)
  // Wrap in an updater: setState treats a bare function arg as a reducer, so we
  // must store the handler via `() => handler`, not pass the setter directly.
  const registerMobileMenu = useCallback((handler: (() => void) | null) => {
    setMobileMenuHandler(() => handler)
  }, [])

  const handleFileDownload = useCallback(async (fileId: string, filename: string) => {
    if (!filesInfo.workspaceId) return
    try {
      const { data } = await api.get(
        `/api/v1/workspaces/${filesInfo.workspaceId}/files/${fileId}/download`
      )
      if (data?.url) {
        const a = document.createElement('a')
        a.href = data.url
        a.download = filename
        a.target = '_blank'
        a.click()
      }
    } catch (err) { console.error('Download failed:', err) }
  }, [filesInfo.workspaceId])

  // Click-outside handler for header dropdowns
  useEffect(() => {
    if (!showNotifications && !showFiles) return
    const handler = (e: MouseEvent) => {
      if (showNotifications && notificationsRef.current && !notificationsRef.current.contains(e.target as Node)) {
        setShowNotifications(false)
      }
      if (showFiles && filesRef.current && !filesRef.current.contains(e.target as Node)) {
        setShowFiles(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showNotifications, showFiles])

  // Check if user is authenticated (either Microsoft or Google)
  const isAuthenticated = accounts.length > 0 || isGoogleAuthenticated

  // Unified logout handler (defined early for session timeout hook)
  const handleLogout = async () => {
    const authProvider = getCurrentAuthProvider()

    if (authProvider === 'google') {
      googleLogout()
      clearGoogleCredential()
      setGoogleUser(null)
      setIsGoogleAuthenticated(false)
    } else {
      // Clear MSAL cache before redirect to prevent stale token reuse
      await instance.clearCache()
      instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin
      })
    }
  }

  // HIPAA: Session timeout after 15 minutes of inactivity
  const { showWarning, remainingSeconds, resetTimer } = useSessionTimeout({
    onLogout: handleLogout,
    enabled: isAuthenticated
  })

  useEffect(() => {
    initializeAuth(instance)
  }, [instance])


  // Check if user needs onboarding
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!isAuthenticated || onboardingChecked) return

      try {
        const response = await api.get('/api/v1/onboarding/status')
        const { needs_onboarding, needs_baa_update, needs_tos_update } = response.data

        if (needs_onboarding || needs_baa_update || needs_tos_update) {
          setShowOnboarding(true)
        }
        setOnboardingChecked(true)
      } catch (error) {
        console.error('Failed to check onboarding status:', error)
        // Allow app access on error to avoid blocking users
        setOnboardingChecked(true)
      }
    }

    checkOnboarding()
  }, [isAuthenticated, onboardingChecked])

  // Restore Google session on page load (skip if dev auth or Microsoft-authenticated)
  useEffect(() => {
    const restoreGoogleSession = async () => {
      if (DEV_AUTH) return
      if (accounts.length > 0) {
        console.log('[Google Auth] Skipping restore - Microsoft account active')
        return
      }

      console.log('[Google Auth] Attempting to restore session...')

      if (restoreGoogleCredential()) {
        try {
          // HIPAA: Use sessionStorage (not localStorage)
          const token = sessionStorage.getItem('google_credential')
          if (token) {
            const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
              headers: { Authorization: `Bearer ${token}` },
            })

            if (!userInfoResponse.ok) {
              clearGoogleCredential()
              return
            }

            const userInfo = await userInfoResponse.json()

            if (!userInfo.email || userInfo.error) {
              clearGoogleCredential()
              return
            }

            setGoogleUser(userInfo)
            setIsGoogleAuthenticated(true)
          }
        } catch (error) {
          console.error('[Google Auth] Failed to restore session:', error)
          clearGoogleCredential()
        }
      }
    }
    restoreGoogleSession()
  }, [accounts.length])

  // Surface and instrument a Google login failure. The previous silent `return`
  // left the user on the login screen with no feedback and emitted nothing to
  // App Insights (the Google path was invisible — only a console.error). Now it
  // shows the shared auth-error banner and records a telemetry event/exception.
  const reportGoogleAuthFailure = (reason: string, error?: unknown) => {
    clearGoogleCredential()
    console.error('[Google Auth] login failed:', reason, error)
    trackAuthEvent('auth.loginFailed', { provider: 'google', reason })
    if (error !== undefined) trackAuthException(error, { source: 'googleLogin', reason })
    setAuthError({ errorCode: reason, aadstsCode: null, source: 'googleLogin' })
  }

  // Google login handler
  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setGoogleCredential(tokenResponse.access_token)

        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        })

        if (!userInfoResponse.ok) {
          reportGoogleAuthFailure(`userinfo_http_${userInfoResponse.status}`)
          return
        }

        const userInfo = await userInfoResponse.json()

        if (!userInfo.email || userInfo.error) {
          reportGoogleAuthFailure('userinfo_no_email')
          return
        }

        setAuthError(null)
        setGoogleUser(userInfo)
        setIsGoogleAuthenticated(true)
        trackAuthEvent('auth.loginSuccess', { provider: 'google' })
      } catch (error) {
        reportGoogleAuthFailure('exception', error)
      }
    },
    onError: (error) => {
      reportGoogleAuthFailure('gsi_error', error)
    },
  })

  // Clear any prior error and record the click before launching the GSI popup,
  // mirroring the Microsoft handler.
  const handleGoogleLoginClick = () => {
    setAuthError(null)
    trackAuthEvent('auth.loginClick', { provider: 'google' })
    handleGoogleLogin()
  }

  // Microsoft login handler
  const handleMicrosoftLogin = async () => {
    setAuthError(null)
    trackAuthEvent('auth.loginClick', { provider: 'microsoft' })

    // Pre-flight: clear any stuck msal.interaction.status so the user never sees
    // a no-op click. Log whether the value matched our clientId (cleaning up
    // our own stale state) vs. an orphan (the bug case we want to fix).
    const valueBefore = readInteractionStatus()
    if (clearStaleInteractionStatus()) {
      trackAuthEvent('auth.preflightCleared', {
        valueMatchedClientId: valueBefore === msalConfig.auth.clientId,
      })
    }

    try {
      await instance.loginRedirect(loginRequest)
    } catch (error) {
      trackAuthException(error, { source: 'loginRedirect' })
      captureAuthError(error, 'loginRedirect')
      setAuthError(consumePendingAuthError())
      console.error('Microsoft login failed:', error)
    }
  }

  // Current user info (handleLogout and isAuthenticated defined above with session timeout hook)
  const currentUser = isGoogleAuthenticated ? googleUser : accounts[0]

  const handleOnboardingComplete = () => {
    setShowOnboarding(false)
  }

  // Extract user name info
  const userFirstName = currentUser?.name?.split(' ')[0] || currentUser?.givenName || 'there'
  const userFullName = currentUser?.name || currentUser?.givenName || 'User'

  return (
    <ThemeProvider defaultTheme="dark">
    <TooltipProvider>
    <div className="min-h-screen bg-background">
      {/* ═══════════════════════════════════════════════════════════════════════════
          UNAUTHENTICATED - Login v6
          ═══════════════════════════════════════════════════════════════════════════ */}
      {!isAuthenticated && (
        <div className="login-page">
          <div className="login-layout">
            {/* Color washes */}
            <div className="login-wash login-wash--sage" />
            <div className="login-wash login-wash--teal-low" />
            <div className="login-wash login-wash--teal" />
            <div className="login-wash login-wash--amber-top" />
            <div className="login-wash login-wash--amber" />
            <div className="login-wash login-wash--cyan" />

            {/* Ecosystem pills */}
            <div className="login-eco-constellation">
              <span className="login-eco-pill login-eco-pill--infra">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                PubMed
              </span>
              <span className="login-eco-pill login-eco-pill--infra">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>
                RxNorm
              </span>
              <span className="login-eco-pill login-eco-pill--infra">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" /></svg>
                Azure Cloud
              </span>
              <span className="login-eco-pill login-eco-pill--infra">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                Azure OpenAI
              </span>
              <span className="login-eco-pill login-eco-pill--tool">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                Chart Review
              </span>
              <span className="login-eco-pill login-eco-pill--tool">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                Reconcile Meds
              </span>
              <span className="login-eco-pill login-eco-pill--tool">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                Diff Dx
              </span>
              <span className="login-eco-pill login-eco-pill--tool">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h7" /></svg>
                Synthesize HPI
              </span>
              <span className="login-eco-pill login-eco-pill--tool">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Orders Review
              </span>
              <span className="login-eco-pill login-eco-pill--tool">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                A&amp;P Synthesis
              </span>
              <span className="login-eco-pill login-eco-pill--tool">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                Admissions Intake
              </span>
            </div>

            {/* Brand */}
            <div className="login-brand">
              <div className="login-brand-wordmark login-boot-left login-boot-wordmark">
                <AcleraWordmark size="md" />
              </div>
              <h1 className="login-brand-title login-boot-left login-boot-title">
                Case<br /><span className="login-brand-title-accent">Panel</span>
              </h1>
              <p className="login-brand-tagline login-boot-left login-boot-tagline">
                Know the <em>story</em> before you walk&nbsp;in.
              </p>
            </div>

            {/* Amber divider */}
            <div className="login-divider login-boot-fade" style={{ animationDelay: '350ms' }} />

            {/* Login card */}
            <div className="login-card login-boot-card login-boot-card-wrap">
              <h2 className="login-card-heading login-boot-content login-boot-buttons" style={{ animationDelay: '500ms' }}>
                Welcome to CasePanel
              </h2>

              {authError && (
                <div
                  role="alert"
                  aria-live="polite"
                  className="login-error-banner"
                  data-error-code={authError.errorCode ?? authError.aadstsCode ?? 'unknown'}
                >
                  <span>{friendlyAuthMessage(authError)}</span>
                  {(authError.aadstsCode ?? authError.errorCode) && (
                    <span className="login-error-code">
                      ({authError.aadstsCode ?? authError.errorCode})
                    </span>
                  )}
                </div>
              )}

              <div className="login-buttons login-boot-content login-boot-buttons">
                <button className="login-btn login-btn--microsoft" onClick={handleMicrosoftLogin}>
                  <span className="login-btn-inner">
                    <svg className="login-provider-icon" viewBox="0 0 23 23" fill="none">
                      <rect width="11" height="11" fill="#F25022" opacity="0.9" />
                      <rect x="12" width="11" height="11" fill="#7FBA00" opacity="0.9" />
                      <rect y="12" width="11" height="11" fill="#00A4EF" opacity="0.9" />
                      <rect x="12" y="12" width="11" height="11" fill="#FFB900" opacity="0.9" />
                    </svg>
                    <span>Continue with Microsoft</span>
                  </span>
                  <svg className="login-btn-chevron" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                </button>

                <button className="login-btn login-btn--google" onClick={handleGoogleLoginClick}>
                  <span className="login-btn-inner">
                    <svg className="login-provider-icon" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    <span>Continue with Google</span>
                  </span>
                  <svg className="login-btn-chevron" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>

              <div className="login-security-banner login-boot-content login-boot-banner">
                <div className="login-security-item">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                  <span>HIPAA</span>
                </div>
                <div className="login-security-sep" />
                <div className="login-security-item">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                  <span>TLS 1.3</span>
                </div>
                <div className="login-security-sep" />
                <div className="login-security-item">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" /></svg>
                  <span>Private</span>
                </div>
                <div className="login-security-sep" />
                <div className="login-security-item">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>
                  <span>Audit</span>
                </div>
              </div>

              <div className="login-card-footer login-boot-fade" style={{ animationDelay: '850ms' }}>
                <span className="login-card-footer-hint">
                  By continuing, you agree to our <a href="https://aclera-ai.com/terms/">Terms</a> and <a href="https://aclera-ai.com/privacy/">Privacy Policy</a>.
                </span>
                <div className="login-card-footer-links">
                  <a href="https://aclera-ai.com/" target="_blank" rel="noopener noreferrer">aclera-ai.com</a>
                  <span className="login-sep" />
                  <a href="mailto:support@aclera-ai.com">Need help?</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════
          AUTHENTICATED - Clinical Workflow Interface
          ═══════════════════════════════════════════════════════════════════════════ */}
      {isAuthenticated && (
        <SubscriptionProvider>
        {/* Pinned to the visual viewport (see useViewportHeight): position:fixed with
            height = visualViewport.height and top = its offsetTop. On iOS (every browser
            is WebKit) the layout viewport — what 100dvh measures — doesn't shrink for the
            soft keyboard, which left dead space below the bottom bar and scrolled a
            focused input into a blank region. Tracking the visual viewport keeps the
            shell exactly over the visible area: no dead space, and when the keyboard
            opens the shell shrinks to sit on top of it. top via `top` (not transform) so
            it never becomes a containing block for the fixed mobile drawer. Falls back to
            top:0 / 100dvh before JS runs; overflow-hidden confines scrolling to the
            inner panes. */}
        <div
          className="fixed inset-x-0 flex flex-col bg-background overflow-hidden"
          style={{ top: 'var(--app-offset-top, 0px)', height: 'var(--app-height, 100dvh)' }}
        >
          {/* Header — 32px unified bar */}
          <header className="flex-none h-12 md:h-8 border-b border-border/50 bg-surface flex items-center px-3 py-1 gap-2">
            {/* Mobile-only: open the sidebar drawer */}
            {mobileMenuHandler && (
              <button
                type="button"
                aria-label="Open navigation menu"
                aria-haspopup="true"
                onClick={() => mobileMenuHandler?.()}
                className="md:hidden flex-shrink-0 -ml-1 flex items-center justify-center min-h-[44px] min-w-[44px] rounded hover:bg-accent transition-colors"
              >
                <Menu className="h-5 w-5 text-muted-foreground" />
              </button>
            )}
            {/* Left: Wordmark — hidden on mobile so the patient/workspace identifier
                gets the scarce header width (the brand returns at md+). */}
            <AcleraWordmark size="sm" className="hidden md:inline text-base flex-shrink-0" />

            {/* Divider — paired with the wordmark, so also desktop-only */}
            <span className="hidden md:inline text-border/60 select-none">|</span>

            {/* Workspace patient label — only shown in workspace phase for admission/flow */}
            {headerInfo.phase === 'workspace' && headerInfo.type !== 'chat' && headerInfo.displayName && (
              <p className="text-xs text-foreground/70 truncate flex-1 min-w-0">
                {headerInfo.displayName}
              </p>
            )}

            {/* Spacer when no patient label */}
            {!(headerInfo.phase === 'workspace' && headerInfo.type !== 'chat' && headerInfo.displayName) && (
              <div className="flex-1" />
            )}

            {/* Workspace action buttons — notifications + files */}
            {headerInfo.phase === 'workspace' && headerInfo.type !== 'chat' && headerInfo.displayName && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <div ref={notificationsRef} className="relative">
                  <button
                    className="relative flex items-center justify-center min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 p-1.5 rounded hover:bg-accent transition-colors"
                    title="Notifications"
                    onClick={() => { setShowNotifications(v => !v); setShowFiles(false) }}
                  >
                    <Bell className="h-4 w-4 text-muted-foreground" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-primary text-[9px] font-bold text-primary-foreground flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  {showNotifications && (
                    <div className="absolute right-0 top-full mt-1 w-[calc(100vw-1.5rem)] max-w-sm rounded-md border border-border bg-popover shadow-md z-50 max-h-96 overflow-hidden flex flex-col">
                      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
                        <span className="text-sm font-medium text-foreground">Notifications</span>
                        <div className="flex items-center gap-2">
                          {unreadCount > 0 && (
                            <button onClick={markAllAsRead} className="text-xs text-primary hover:underline">
                              Mark all read
                            </button>
                          )}
                          {notifications.length > 0 && (
                            <button onClick={() => { clearNotifications(); setShowNotifications(false) }} className="text-xs text-muted-foreground hover:text-destructive hover:underline">
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="overflow-y-auto flex-1">
                        {notifications.length === 0 ? (
                          <p className="text-sm text-muted-foreground p-4">No notifications yet — agent activity will appear here.</p>
                        ) : (
                          notifications.map(n => (
                            <button
                              key={n.id}
                              onClick={() => markAsRead(n.id)}
                              className={cn(
                                'w-full text-left px-3 py-2.5 text-sm border-b border-border/30 hover:bg-muted/50 transition-colors',
                                !n.read && 'bg-primary/5'
                              )}
                            >
                              <div className="flex items-start gap-2">
                                <span className={cn(
                                  'w-2 h-2 rounded-full mt-1.5 flex-shrink-0',
                                  n.type === 'clinical_alert' ? 'bg-amber-400' :
                                  n.type === 'content_change' ? 'bg-purple-400' :
                                  'bg-blue-400'
                                )} />
                                <div className="min-w-0">
                                  <p className="text-foreground/80 truncate">{n.message}</p>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {new Date(n.timestamp).toLocaleTimeString()}
                                  </p>
                                </div>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div ref={filesRef} className="relative">
                  <button
                    className="flex items-center justify-center min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 p-1.5 rounded hover:bg-accent transition-colors"
                    title="Files"
                    onClick={() => { setShowFiles(v => !v); setShowNotifications(false) }}
                  >
                    <FolderOpen className="h-4 w-4 text-muted-foreground" />
                  </button>
                  {showFiles && (
                    <div className="absolute right-0 top-full mt-1 w-[calc(100vw-1.5rem)] max-w-sm rounded-md border border-border bg-popover shadow-md z-50 max-h-96 overflow-hidden flex flex-col">
                      <div className="px-3 py-2.5 border-b border-border">
                        <span className="text-sm font-medium text-foreground">Files</span>
                      </div>
                      <div className="overflow-y-auto flex-1">
                        {/* Uploaded Files section */}
                        {filesInfo.uploadedFiles.length > 0 && (
                          <div className="px-3 py-2">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Uploaded Files</p>
                            {filesInfo.uploadedFiles.map((f) => (
                              <button
                                key={f.id}
                                onClick={() => handleFileDownload(f.id, f.name)}
                                className="w-full flex items-center gap-2 py-1.5 text-sm text-foreground/80 hover:bg-muted/50 rounded px-1 -mx-1 transition-colors"
                                title={`Download ${f.name}`}
                              >
                                <FileText className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                <span className="truncate flex-1 text-left">{f.name}</span>
                                <Download className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Agent Artifacts section */}
                        {filesInfo.artifacts.length > 0 && (
                          <div className={cn("px-3 py-2", filesInfo.uploadedFiles.length > 0 && "border-t border-border/30")}>
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Agent Artifacts</p>
                            {filesInfo.artifacts.map((a) => (
                              <button
                                key={a.id}
                                onClick={() => {
                                  const tabMap: Record<string, string> = {
                                    hpi: 'hpi', pe: 'pe', assessment_and_plan: 'assessment_and_plan',
                                    ap: 'assessment_and_plan', hpi_pe: 'hpi', notes: 'notes',
                                  }
                                  canvasTabRef.current?.(tabMap[a.type] || 'hpi')
                                  setShowFiles(false)
                                }}
                                className="w-full flex items-center gap-2 py-1.5 text-sm text-foreground/80 hover:bg-muted/50 rounded px-1 -mx-1 transition-colors"
                              >
                                <Sparkles className="h-4 w-4 text-purple-400 flex-shrink-0" />
                                <span className="truncate flex-1 text-left">{a.title}</span>
                                <span className="text-xs text-muted-foreground flex-shrink-0">{a.type}</span>
                              </button>
                            ))}
                          </div>
                        )}

                        {filesInfo.uploadedFiles.length === 0 && filesInfo.artifacts.length === 0 && (
                          <p className="text-sm text-muted-foreground p-4">No files in this workspace.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Logout */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  size="icon"
                  className="w-6 h-6 rounded hover:bg-destructive/10 hover:text-destructive transition-all duration-300"
                >
                  <LogOut className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="text-xs">Sign out</p>
              </TooltipContent>
            </Tooltip>
          </header>

          {/* Main Workflow */}
          <main className="flex-1 min-h-0 overflow-hidden flex flex-col">
            <SubscriptionGate>
                  <ProjectsProvider>
                    <ChatProvider>
                      <EncounterProvider>
                        <WorkspaceProvider>
                          <WorkspaceManager userFirstName={userFirstName} userFullName={userFullName} onOpenSettings={() => setSettingsOpen(true)} onHeaderChange={setHeaderInfo} onAddNotification={addNotification} onFilesChange={setFilesInfo} canvasTabRef={canvasTabRef} onRegisterMobileMenu={registerMobileMenu} onLoadNotifications={loadNotifications} />
                        </WorkspaceProvider>
                      </EncounterProvider>
                    </ChatProvider>
                  </ProjectsProvider>
            </SubscriptionGate>
          </main>

          {/* Footer */}
          <footer className="hidden md:block flex-none bg-surface border-t border-border/50">
            <div className="px-4 py-2 flex items-center justify-center gap-6">
              <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                <Lock className="h-3 w-3" />
                <span>TLS 1.2+ Encrypted</span>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                <Shield className="h-3 w-3" />
                <span>HIPAA Compliant</span>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                <Cloud className="h-3 w-3" />
                <span>Azure Secured</span>
              </div>
            </div>
          </footer>

          {/* Settings Dialog */}
          <SettingsDialog
            open={settingsOpen}
            onOpenChange={setSettingsOpen}
            defaultTab="subscription"
          />

          {/* Onboarding Wizard */}
          <OnboardingWizard
            open={showOnboarding}
            onComplete={handleOnboardingComplete}
          />

          {/* HIPAA: Session Timeout Warning Modal - §164.312(a)(2)(iii) */}
          {showWarning && (
            <SessionTimeoutWarning
              remainingSeconds={remainingSeconds}
              onContinue={resetTimer}
            />
          )}
        </div>
        </SubscriptionProvider>
      )}
    </div>
    </TooltipProvider>
    </ThemeProvider>
  )
}

export default App
